import { apiClient } from '@lib/api-client'
import { getBoundaryApi } from 'api-validations/locutus'
import { contourApi } from 'api-validations/stitch'
import * as Comlink from 'comlink'
import { useOrgCodeOrThrow } from 'shared-ui/active-operation'
import { useMutation } from 'shared-ui/api-provider'
import { useStitchRequestType } from 'shared-ui/field-data'
import { useLocalization } from 'shared-ui/localization'
import {
  createZone,
  useAsAppliedLayerOrNull,
  useDefaultLegendConfig,
  useMapContext,
  useUserFacingLegend
} from 'shared-ui/map'
import { generateAlternatingRanges, isNil } from 'utils'

import { WorkerApi } from './Geo.worker'
// oxlint-disable-next-line import/default -- Vite virtual module (?worker&url) resolved at build time
import workerUrl from './Geo.worker?worker&url'

export const useGeneratePrescription = () => {
  const { formatMetric } = useLocalization()
  const defaultConfig = useDefaultLegendConfig().data

  const { continuous } = useUserFacingLegend()

  const unit = defaultConfig?.unit

  const { map } = useMapContext()
  // Nullable so the hook can mount under any selection (e.g. the plan editor);
  // auto-generate itself is gated on an as-applied layer being selected.
  const activeDataLayer = useAsAppliedLayerOrNull(map.isComparison())

  const orgCode = useOrgCodeOrThrow()
  const requestType = useStitchRequestType()

  return useMutation({
    mutationFn: async (boundaryId: string) => {
      if (isNil(activeDataLayer)) {
        throw new Error('Auto-generating a prescription requires an as-applied layer selection')
      }
      const worker = new Worker(workerUrl, { type: 'module' })

      try {
        const workerApi = Comlink.wrap<WorkerApi>(worker)

        const boundary = getBoundaryApi.responseSchema.parse(
          (
            await apiClient.get(
              getBoundaryApi.pathFor({
                boundaryId,
                orgCode
              })
            )
          ).body
        )

        const high = continuous.at(-1)?.min ?? 0
        const low = continuous.at(0)?.max ?? 0
        const steps = continuous.length

        const step = (high - low) / (steps - 2)

        const rangeArray = generateAlternatingRanges(steps, step, { low })

        if (isNil(boundary.data)) {
          return
        }

        const { streamId, applicationId, layerId, mergedStreamIds } = activeDataLayer

        const rates = await Promise.all(
          rangeArray.slice(1).map(async (value) => {
            const contours = await Promise.all(
              [streamId, ...mergedStreamIds].map((streamId) =>
                apiClient.callApi(contourApi, {
                  orgCode,
                  applicationId,
                  layerId,
                  streamId,
                  requestType,
                  epsilon: 0.1,
                  tolerance: 1,
                  minFeature: 0.1,
                  ...(value.type === 'high'
                    ? { min: value.min - 0.001 }
                    : { max: value.max + 0.001 })
                })
              )
            )
            return {
              range: value,
              contour: {
                type: 'FeatureCollection' as const,
                features: contours.flatMap((contour) => contour.features)
              }
            }
          })
        )
        const smoothed = await workerApi.smoothPolygons({
          boundary,
          baseRange: rangeArray[0],
          rates
        })
        if (isNil(smoothed)) {
          throw new Error(
            'There was an error generating your prescription, we were unable to create zones '
          )
        }
        const { zones, featureCollection } = smoothed

        return {
          featureCollection,
          zones: zones
            .toSorted((a, b) => a.min - b.min)
            .map(({ min, max, key }) =>
              createZone({
                id: key,
                name: `${formatMetric({ value: min, unit }, { includeUnits: false })} - ${formatMetric({ value: max, unit }, { includeUnits: false })}`
              })
            )
        }
      } finally {
        worker.terminate()
      }
    },
    onSuccess: (result) => {
      map.drawTool.clear()
      result?.featureCollection?.features.forEach((feat) => {
        map.drawTool.addFeatures(feat)
      })
    }
  })
}
